import { defineStore } from "pinia";
import { useBoardLayerStore } from "./layer";
import { ref } from "vue";

// 历史记录节点类型
interface HistoryNode {
  id: string;
  timestamp: number;
  layersSnapshot: string; // 序列化的图层状态
  description: string;
}

export const useBoardHistoryStore = defineStore("infinite-canvas-history", () => {
  // State
  const undoStack = ref<HistoryNode[]>([]);
  const redoStack = ref<HistoryNode[]>([]);
  const currentState = ref<HistoryNode | null>(null);
  const maxSteps = ref(50); // 最大历史记录步数
  const debounceTimer = ref<ReturnType<typeof setTimeout> | null>(null);
  const isRestoringHistory = ref(false); // 是否正在恢复历史记录，用于避免在恢复过程中触发新的历史记录
  const syncDataHandler = ref<(() => void) | null>(null);
  const restoreStateHandler = ref<((state: HistoryNode) => Promise<void> | void) | null>(null); // 状态恢复处理器

  // Actions
  function setSyncHandler(handler: () => void) {
    syncDataHandler.value = handler;
  }

  /**
   * 设置状态恢复处理器
   * @param handler - 接收历史状态并恢复画布的函数
   */
  function setRestoreStateHandler(handler: (state: HistoryNode) => Promise<void> | void) {
    restoreStateHandler.value = handler;
  }

  /**
   * 设置初始状态（用于画布加载完成后）
   * 只设置 currentState，不推入 undoStack，不清空 redoStack
   */
  function setInitialState(description: string) {
    const layerStore = useBoardLayerStore();

    currentState.value = {
      id: Date.now().toString(),
      timestamp: Date.now(),
      layersSnapshot: layerStore.generateSnapshot(),
      description
    };
  }

  function recordHistory(description: string, immediate = false) {
    // 如果正在恢复历史记录，则不记录新的历史
    if (isRestoringHistory.value) return;

    if (debounceTimer.value) clearTimeout(debounceTimer.value);

    const record = () => {
      // 1. 尝试同步最新数据
      if (syncDataHandler.value) {
        syncDataHandler.value();
      }

      const layerStore = useBoardLayerStore();
      const newSnapshot = layerStore.generateSnapshot();

      // 如果当前有状态，且新快照与当前状态一致，则跳过（避免无效的历史记录）
      // 比如：删除占位符时，由于占位符本身不包含在快照中，导致删除前后的快照一致，此时不需要记录历史
      if (currentState.value && currentState.value.layersSnapshot === newSnapshot) {
        return;
      }

      // 如果当前有状态，先推入undo栈
      if (currentState.value) {
        undoStack.value.push(currentState.value);

        // 超出最大步数时移除最旧记录
        if (undoStack.value.length > maxSteps.value) {
          undoStack.value.shift();
        }
      }

      // 清空重做栈
      redoStack.value = [];

      // 创建新状态快照
      currentState.value = {
        id: Date.now().toString(),
        timestamp: Date.now(),
        layersSnapshot: newSnapshot,
        description
      };

      debounceTimer.value = null;
    };

    if (immediate) {
      record();
    } else {
      debounceTimer.value = setTimeout(record, 300);
    }
  }

  async function undo() {
    if (undoStack.value.length ===0) return null;

    // 标记正在恢复历史，避免触发新的历史记录
    isRestoringHistory.value = true;

    try {
      // 当前状态推入重做栈
      if (currentState.value) {
        redoStack.value.push(currentState.value);
      }

      // 获取上一个状态
      const prevState = undoStack.value.pop()!;
      currentState.value = prevState;

      // 调用恢复处理器恢复画布状态
      if (restoreStateHandler.value) {
        await restoreStateHandler.value(prevState);
      }

      return prevState;
    } finally {
      isRestoringHistory.value = false;
    }
  }

  async function redo() {
    if (redoStack.value.length === 0) return null;

    // 标记正在恢复历史，避免触发新的历史记录
    isRestoringHistory.value = true;

    try {
      // 当前状态推入撤销栈
      if (currentState.value) {
        undoStack.value.push(currentState.value);
      }

      // 获取重做状态
      const nextState = redoStack.value.pop()!;
      currentState.value = nextState;

      // 调用恢复处理器恢复画布状态
      if (restoreStateHandler.value) {
        await restoreStateHandler.value(nextState);
      }

      return nextState;
    } finally {
      isRestoringHistory.value = false;
    }
  }

  function setIsRestoringHistory(value: boolean) {
    isRestoringHistory.value = value;
  }

  function reset() {
    undoStack.value = [];
    redoStack.value = [];
    currentState.value = null;
    isRestoringHistory.value = false;
    if (debounceTimer.value) clearTimeout(debounceTimer.value);
  }

  return {
    undoStack,
    redoStack,
    currentState,
    maxSteps,
    debounceTimer,
    isRestoringHistory,

    recordHistory,
    undo,
    redo,
    setIsRestoringHistory,
    setSyncHandler,
    setRestoreStateHandler,
    setInitialState,
    reset
  };
});
